// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
  useColorScheme,
} from 'react-native';
import { createTheme } from '../design-system/theme';
import { threadsStrings } from './strings';

export interface ThreadReplyComposerProps {
  readonly onSend: (body: string) => Promise<void>;
  readonly sending?: boolean;
  readonly disabled?: boolean;
  readonly maxLength?: number;
  readonly testID?: string;
}

const DEFAULT_MAX_LENGTH = 4000;

/**
 * Thread-scoped composer. Mirrors `ChatRoomComposer` shape but is
 * configured for the thread context — no reply-to chrome, no
 * conversation-scoped placeholder. The host (typically `ThreadOpenPanel`)
 * forwards the send intent to the view-model's `sendReply`.
 *
 * Draft text lives in this component only — on unmount, it is dropped.
 * The follow-up slice can promote it into `composer/draftStorage.ts` if
 * the conversation surface demands persistence.
 */
export const ThreadReplyComposer: React.FC<ThreadReplyComposerProps> = ({
  onSend,
  sending = false,
  disabled = false,
  maxLength = DEFAULT_MAX_LENGTH,
  testID,
}) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const theme = createTheme(colorScheme);
  const inputRef = useRef<TextInput>(null);
  const [text, setText] = useState('');

  const isBusy = sending;
  const isDisabled = disabled;
  const canSend = text.trim().length > 0 && !isDisabled && !isBusy;

  const handleSend = useCallback(async (): Promise<void> => {
    if (!canSend) {
      return;
    }
    const body = text.trim();
    setText('');
    try {
      await onSend(body);
    } catch {
      // Restore the draft so the user can retry. The VM is responsible
      // for logging; we do not log here to avoid duplicate sinks.
      setText(body);
    }
  }, [canSend, onSend, text]);

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface.primary,
          borderTopColor: theme.colors.border.secondary,
        },
      ]}
      testID={testID}
    >
      <TextInput
        ref={inputRef}
        style={[
          styles.input,
          {
            color: theme.colors.text.primary,
            backgroundColor: theme.colors.surface.secondary,
          },
        ]}
        placeholder={
          isDisabled
            ? threadsStrings.ping_threads_composer_disabled_hint
            : threadsStrings.ping_threads_composer_placeholder
        }
        placeholderTextColor={theme.colors.text.tertiary}
        value={text}
        onChangeText={setText}
        editable={!isDisabled && !isBusy}
        maxLength={maxLength}
        multiline
        accessibilityLabel={threadsStrings.ping_threads_composer_placeholder}
      />
      <Pressable
        onPress={() => {
          void handleSend();
        }}
        disabled={!canSend}
        accessibilityRole="button"
        accessibilityLabel={threadsStrings.ping_threads_composer_send}
        accessibilityState={{ disabled: !canSend, busy: isBusy }}
        style={({ pressed }) => [
          styles.sendButton,
          { backgroundColor: theme.colors.accent.indigo },
          !canSend && styles.disabled,
          pressed && canSend && styles.pressed,
        ]}
      >
        <Text style={[styles.sendButtonText]}>{threadsStrings.ping_threads_composer_send}</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  input: {
    flex: 1,
    minHeight: 40,
    maxHeight: 120,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    fontSize: 14,
    lineHeight: 20,
  } satisfies TextStyle,
  sendButton: {
    height: 40,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  sendButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  disabled: {
    opacity: 0.5,
  } satisfies ViewStyle,
  pressed: {
    opacity: 0.85,
  } satisfies ViewStyle,
});
